import { NaReason } from '../types';

export type NotApplicableActionLink =
  | { type: 'category'; category: string }
  | { type: 'route'; to: string };

export interface NotApplicableInfo {
  label?: string;
  reason: string;
  action?: string;
  actionLink?: NotApplicableActionLink;
}

const REASON_LABELS: Record<NaReason, string> = {
  not_applicable: 'Not Applicable',
  no_data: 'No Data Yet',
  no_ownership_confirmed: 'Ownership Not Confirmed',
};

const NA_JIRA_TEAMS: NotApplicableInfo = {
  reason:
    "This metric is calculated from your team's Jira Teams, and does not apply because your team owns none.",
  action:
    'Claim Jira Teams ownership and ensure issues are also assigned to a Jira Team',
  actionLink: {
    type: 'route',
    to: '/jira-teams?filters%5Bkind%5D=component&filters%5Btype%5D=jira-team&filters%5Buser%5D=all',
  },
};

const NA_REPOSITORIES: NotApplicableInfo = {
  reason:
    'This metric is calculated from repositories your team owns, and does not apply because your team owns none.',
};

const NA_VMS: NotApplicableInfo = {
  reason:
    'This metric applies only to teams that own virtual machines (VMs), and does not apply because your team does not own any.',
};

const NA_KEY_ROTATION: NotApplicableInfo = {
  reason:
    'Key Rotation tracks whether secrets for Azure Service Principals and AWS IAM roles are rotated at least every 90 days. It does not apply because your team does not own any such credentials.',
};

const OWNERSHIP_NOT_CONFIRMED: NotApplicableInfo = {
  reason:
    'This metric is calculated from repositories your team owns, but no repository ownership has been confirmed yet.',
  action: 'Confirm your repository ownership',
  actionLink: { type: 'category', category: 'Repository Ownership' },
};

const REASON_DEFAULTS: Record<NaReason, NotApplicableInfo> = {
  not_applicable: {
    reason:
      'This metric does not apply to your team, so there is nothing to score for the selected period.',
  },
  no_data: {
    reason:
      'No data has been collected yet for the selected period. This is common early in a period, or when there was no activity.',
  },
  no_ownership_confirmed: OWNERSHIP_NOT_CONFIRMED,
};

const CATEGORY_OVERRIDES: Record<
  string,
  Partial<Record<NaReason, NotApplicableInfo>>
> = {
  'Orphaned Issues': { not_applicable: NA_JIRA_TEAMS },
  'Investment Category': { not_applicable: NA_JIRA_TEAMS },
  KTLO: { not_applicable: NA_JIRA_TEAMS },
  'Commit Signature Verification': { not_applicable: NA_REPOSITORIES },
  'CI/CD Success Rate': { not_applicable: NA_REPOSITORIES },
  Velocity: { not_applicable: NA_REPOSITORIES },
  'Repository Ownership': { not_applicable: NA_REPOSITORIES },
  'Key Rotation': { not_applicable: NA_KEY_ROTATION },
  'End of Life': { not_applicable: NA_VMS },
  'Agent Coverage': { not_applicable: NA_VMS },
  'Public IP': { not_applicable: NA_VMS },
  'VM Vulnerability Compliance': { not_applicable: NA_VMS },
};

export const getNotApplicableInfo = (
  category: string,
  naReason?: NaReason | null,
): NotApplicableInfo | undefined => {
  if (!naReason) return undefined;
  const base =
    CATEGORY_OVERRIDES[category]?.[naReason] ?? REASON_DEFAULTS[naReason];
  return { label: REASON_LABELS[naReason], ...base };
};
